import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { useMediaQuery } from 'react-responsive';
import { TypedMemo } from '../../common/types';
import { SmartLinkManageViewRendererProps } from './props';
import { SmartLinkManageStats } from './SmartLinkManageStats';
import { SMLContainer } from '../smlContainer/SMLContainer';
import { SMLPageHead } from '../../music24/components/SMLPageHead/SMLPageHead';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import styles from './SmartLinkManageView.module.scss';
import { SMLPreview } from '../smlPreview/SMLPreview';
import { PresaveCampaignPanel } from '../presave/PresaveCampaignPanel';
import { SMLPageActions } from '../pageActions/SMLPageActions';
import { SMLPageMeta } from '../pageActions/SMLPageMeta';
import { SMLPageUrlBar } from '../pageActions/SMLPageUrlBar';
import { useSmartLinkPageFacts } from '../pageActions/useSmartLinkPageFacts';
import { useExportPresaves } from '../presave/useExportPresaves';
import { useUnpublishPageFlow } from '../unpublishPage/useUnpublishPageFlow';
import { SMLUnpublishPageModal } from '../unpublishPage/SMLUnpublishPageModal';

const SmartLinkManageViewRendererComponent: React.FC<SmartLinkManageViewRendererProps> = ({
  smartLink,
  openInWeb,
  openEmbedModal,
  goToSmartLinkDetailsView,
  goToSmartLinkDuplicationView,
  goToSmartLinkAddressEdition,
  onDelete,
  disabledEmbedCode,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const {
    shareUrl,
    recentSmartLinkVersion: { name, slug },
    isMainSmartLink,
  } = smartLink;

  const facts = useSmartLinkPageFacts(smartLink.accountId, smartLink);
  const title = isMainSmartLink ? intl.formatMessage({ id: 'app.sml.bio-link', defaultMessage: 'Bio Link' }) : name;

  // Both live here rather than in the container: neither leaves the screen, and
  // the mobile app has its own renderer with neither action on it.
  const {
    exportPresaves,
    canExport,
    isLocked: isExportLocked,
  } = useExportPresaves({
    accountId: smartLink.accountId,
    campaignUuid: facts.campaignUuid,
  });
  const unpublishFlow = useUnpublishPageFlow({ accountId: smartLink.accountId, smartLinkId: smartLink.id });

  const breadcrumbs = useMemo(() => {
    return [
      {
        name: intl.formatMessage({ id: 'app.sml.sidebar.section.promote', defaultMessage: 'Promote' }),
      },
      {
        name: intl.formatMessage({ id: 'app.sml.breadcrumbs.smartlinks', defaultMessage: 'SmartLinks' }),
        onClick: () => router.push(SMLRoutes.smartlinks),
      },
      {
        name: title,
      },
    ];
  }, [intl, router, title]);
  const isBootstrapLessThanLG = useMediaQuery({ query: '(max-width: 991px)' });

  return (
    <SMLContainer
      fullHeight
      header={
        <SMLPageHead
          breadcrumbs={breadcrumbs}
          title={title}
          meta={<SMLPageMeta facts={facts} createdAt={smartLink.createdAt} />}
        >
          <SMLPageActions
            onEdit={goToSmartLinkDetailsView}
            onOpen={openInWeb}
            onDuplicate={goToSmartLinkDuplicationView}
            onChangeAddress={goToSmartLinkAddressEdition}
            /* An unpublished page has no snippet to hand out yet, and neither
               has one whose embed url never arrived. */
            onEmbed={disabledEmbedCode || smartLink.embedUrl === null ? undefined : openEmbedModal}
            /* Nothing to export until the page carries a campaign. Free still shows the item —
               it opens the upgrade prompt instead of the file. */
            onExportPresaves={canExport ? exportPresaves : undefined}
            isExportLocked={isExportLocked}
            /* Only a live page can be taken offline, and the bio link is what
               the account's own address serves — it has no offline state. */
            onUnpublish={facts.isPublished && !isMainSmartLink ? unpublishFlow.openModal : undefined}
            /* The bio link is the account's own page and cannot be deleted. */
            onDelete={isMainSmartLink ? undefined : onDelete}
          />
        </SMLPageHead>
      }
    >
      <SMLUnpublishPageModal
        isOpen={unpublishFlow.isModalOpen}
        accountId={smartLink.accountId}
        smartLink={smartLink}
        isUnpublishing={unpublishFlow.isUnpublishing}
        closeModal={unpublishFlow.closeModal}
        onUnpublish={unpublishFlow.unpublish}
      />

      <SMLPageUrlBar shareUrl={shareUrl} slug={slug} isPublished={facts.isPublished} />

      <Row className={styles.fillRow}>
        <Col xs={12} lg={6} className={styles.statsColumn}>
          {/* The campaign sits above the stats: it is the thing the artist acts
              on. It renders nothing unless the page carries a pre-save block. */}
          <div className={styles.presavePanel}>
            <PresaveCampaignPanel accountId={smartLink.accountId} smartLink={smartLink} />
          </div>

          <SmartLinkManageStats smartLink={smartLink} />
        </Col>
        <Col xs={12} lg={6} className="d-none d-lg-flex flex-column">
          {!isBootstrapLessThanLG && <SMLPreview smartLink={smartLink} type="column" showPublished />}
        </Col>
      </Row>
    </SMLContainer>
  );
};

export const SmartLinkManageViewRenderer = TypedMemo(SmartLinkManageViewRendererComponent);
